import { addDoc, collection } from "firebase/firestore";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { Close } from "@mui/icons-material";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";

interface UploadEMagazineTabProps {
  onSuccess?: () => void;
}

function normalizeUrl(value: string) {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  if (
    trimmedValue.startsWith("http://") ||
    trimmedValue.startsWith("https://") ||
    trimmedValue.startsWith("/")
  ) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
}

export default function UploadEMagazineTab({ onSuccess }: UploadEMagazineTabProps) {
  const { showSnackbar } = useFeedback();
  const [title, setTitle] = useState("");
  const [issue, setIssue] = useState("");
  const [description, setDescription] = useState("");
  const [releaseDate, setReleaseDate] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [selectedCoverImage, setSelectedCoverImage] = useState<File | null>(null);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const submitLockRef = useRef(false);

  async function handleSubmit() {
    if (submitLockRef.current) {
      return;
    }

    if (!title.trim() || !releaseDate.trim() || !selectedPdfFile) {
      showSnackbar("Please fill title, release date, and upload the PDF file");
      return;
    }

    try {
      submitLockRef.current = true;
      setIsSaving(true);
      const timestamp = new Date().toISOString();
      let finalCoverImage = coverImage.trim();

      if (selectedCoverImage) {
        const coverRef = ref(storage, `yuvagaaz/emagazine-covers/${Date.now()}-${selectedCoverImage.name}`);
        await uploadBytes(coverRef, selectedCoverImage);
        finalCoverImage = await getDownloadURL(coverRef);
      }

      const pdfRef = ref(storage, `yuvagaaz/emagazines/${Date.now()}-${selectedPdfFile.name}`);
      await uploadBytes(pdfRef, selectedPdfFile);
      const pdfUrl = await getDownloadURL(pdfRef);

      await addDoc(collection(firestore, "yuvagaaz_emagazines"), {
        title: title.trim(),
        issue: issue.trim(),
        description: description.trim(),
        releaseDate: releaseDate.trim(),
        coverImage: normalizeUrl(finalCoverImage),
        pdfUrl,
        createdAt: timestamp,
        updatedAt: timestamp,
      });

      setTitle("");
      setIssue("");
      setDescription("");
      setReleaseDate("");
      setCoverImage("");
      setSelectedCoverImage(null);
      setSelectedPdfFile(null);
      showSnackbar("E-magazine added successfully");
      onSuccess?.();
    } catch (error) {
      console.error("Error adding e-magazine", error);
      showSnackbar("Failed to add e-magazine");
    } finally {
      setIsSaving(false);
      submitLockRef.current = false;
    }
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField fullWidth label="Title" value={title} onChange={(event) => setTitle(event.target.value)} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Issue / Subtitle"
            value={issue}
            onChange={(event) => setIssue(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Release Date"
            type="date"
            value={releaseDate}
            onChange={(event) => setReleaseDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Cover Image URL"
            value={coverImage}
            onChange={(event) => {
              setCoverImage(event.target.value);
              if (event.target.value) {
                setSelectedCoverImage(null);
              }
            }}
            helperText="Use either a cover image URL or upload an image below."
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Upload Cover Image</FormLabel>
            <input
              type="file"
              accept="image/*"
              style={{
                border: "1px solid rgb(181, 176, 176)",
                width: "96%",
                paddingLeft: 8,
                paddingRight: 8,
                paddingTop: 16,
                paddingBottom: 16,
                borderRadius: 5,
                marginTop: 8,
              }}
              onChange={(event) => {
                if (event.target.files?.[0]) {
                  setSelectedCoverImage(event.target.files[0]);
                  setCoverImage("");
                }
              }}
            />
            {selectedCoverImage ? (
              <Box mt={2} position="relative" display="inline-block">
                <img
                  src={URL.createObjectURL(selectedCoverImage)}
                  alt="E-magazine cover preview"
                  style={{ maxWidth: "220px", maxHeight: "180px", borderRadius: 8 }}
                />
                <IconButton
                  size="small"
                  onClick={() => setSelectedCoverImage(null)}
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    color: "#ffffff",
                    backgroundColor: "rgba(0,0,0,0.5)",
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
            ) : null}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Upload E-Magazine PDF</FormLabel>
            <input
              type="file"
              accept="application/pdf"
              style={{
                border: "1px solid rgb(181, 176, 176)",
                width: "96%",
                paddingLeft: 8,
                paddingRight: 8,
                paddingTop: 16,
                paddingBottom: 16,
                borderRadius: 5,
                marginTop: 8,
              }}
              onChange={(event) => setSelectedPdfFile(event.target.files?.[0] || null)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save E-Magazine"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
